import { TreeNode } from "@/types/tree";
import { UnsplashImage } from "../api/unsplash";
import { GALLERY_SLUG, SECTION_SLUG } from "@/lib/elements/constants";

export function isUnsplashImage(image: UnsplashImage): boolean {
  return image.urls.raw.includes("unsplash");
}

export function isPexelsImage(image: UnsplashImage): boolean {
  return image.urls.raw.includes("pexels");
}

export function isPixabayImage(image: UnsplashImage): boolean {
  return image.urls.raw.includes("pixabay");
}

export function getUnsplashImageUrlWithWidth(
  rawUrl: string,
  width: number
): string {
  return `${rawUrl}&w=${width}&dpr=2`;
}

export function getPexelsImageUrlWithWidth(
  rawUrl: string,
  width: number
): string {
  const url = new URL(rawUrl);
  url.searchParams.set("w", (width * 2).toString()); // double the width for dpr=2 like unsplash
  // Keep the original height ratio by removing h parameter to let Pexels auto-calculate
  url.searchParams.delete("h");
  return url.toString();
}

export function getPixabayImageUrlWithWidth(
  rawUrl: string,
  _width: number
): string {
  // Pixabay doesn't support dynamic resizing via URL params like Unsplash/Pexels
  // We'll return the original URL and let the browser handle scaling
  return rawUrl;
}

export function getLikelyWidthForImage(node: TreeNode): number | undefined {

  // assume images are never wider than the default section container which would be 1120, minus 20px padding on either side. *2 for retina.
  let retVal = 1080*2;

  if (node.data.type === SECTION_SLUG) {
    retVal = 2880; // 1440px * 2 for section backgrounds, we want these big and beautiful
  }

  if (node.data.type === GALLERY_SLUG) {
    retVal = 2880; // 1440px * 2 for gallery images, we want these big and beautiful
  }

  const storedWidth = node.data.properties?.meta?.imageDimensions?.renderedWidthPx;

  if (typeof storedWidth === "number" && storedWidth > 0) {
    retVal = storedWidth;
  } else {
    console.warn('no stored width for image for', node.data.type, node.id);
  }

  return retVal;
}

export function getOptimalImageUrl(
  image: UnsplashImage,
  desiredWidth?: number
): string {
  if (isUnsplashImage(image)) {
    if (desiredWidth) {
      return getUnsplashImageUrlWithWidth(image.urls.raw, desiredWidth);
    }
    return image.urls.regular;
  } else if (isPexelsImage(image)) {
    if (desiredWidth) {
      return getPexelsImageUrlWithWidth(image.urls.raw, desiredWidth);
    }
    return image.urls.regular;
  } else if (isPixabayImage(image)) {
    if (desiredWidth) {
      return getPixabayImageUrlWithWidth(image.urls.raw, desiredWidth);
    }
    return image.urls.regular;
  } else {
    // Fallback for unknown sources
    return image.urls.regular;
  }
}

export function getImageSourceName(image: UnsplashImage): string {
  if (isUnsplashImage(image)) {
    return "Unsplash";
  } else if (isPexelsImage(image)) {
    return "Pexels";
  } else if (isPixabayImage(image)) {
    return "Pixabay";
  } else {
    return "Unknown Source";
  }
}

export function getAuthorProfileUrl(image: UnsplashImage): string | null {
  if (isUnsplashImage(image)) {
    return `<a href="https://unsplash.com/@"${image.user.username}?" target="_blank" rel="noopener noreferrer">${image.user.name}</a>`;
  } else if (isPexelsImage(image)) {
    return `<a href="https://pexels.com/@${image.user.username}" target="_blank" rel="noopener noreferrer">${image.user.name}</a>`;
  } else if (isPixabayImage(image)) {
    return `<a href="https://pixabay.com/users/${image.user.username}" target="_blank" rel="noopener noreferrer">${image.user.name}</a>`;
  } else {
    return null;
  }
}

export function getImageAttribution(image: UnsplashImage): string {
  if (isUnsplashImage(image)) {
    return `Photo by ${getAuthorProfileUrl(
      image
    )} on <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">Unsplash</a>`;
  } else if (isPexelsImage(image)) {
    return `Photo by ${getAuthorProfileUrl(
      image
    )} on <a href="https://pexels.com" target="_blank" rel="noopener noreferrer">Pexels</a>`;
  } else if (isPixabayImage(image)) {
    return `Photo by ${getAuthorProfileUrl(
      image
    )} on <a href="https://pixabay.com" target="_blank" rel="noopener noreferrer">Pixabay</a>`;
  } else {
    return `Photo by ${image.user.name}`;
  }
}

export function getImageOrientation(
  image: UnsplashImage
): "landscape" | "portrait" | "square" {
  if (image.width > image.height) {
    return "landscape";
  } else if (image.width < image.height) {
    return "portrait";
  } else {
    return "square";
  }
}

export function getImageAspectRatio(image: UnsplashImage): number {
  return image.width / image.height;
}
